import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { ArrowUpRight, Calculator, Receipt, Users, Sparkles, HandCoins } from "lucide-react";

const items = [
  {
    slug: "comptabilite", icon: Calculator, title: "Comptabilité & Bilan",
    tag: "Tenue · Révision · Bilan",
    lede: "Une gestion comptable rigoureuse pour piloter votre entreprise en toute sérénité.",
    bullets: [
      "Tenue comptable mensuelle ou trimestrielle",
      "Révision et clôture des comptes annuels",
      "Établissement des comptes annuels (bilan, compte de résultat, annexes)",
      "Reporting financier personnalisé",
      "Interface digitale pour vos pièces comptables",
    ],
  },
  {
    slug: "fiscalite", icon: Receipt, title: "Fiscalité & Impôts",
    tag: "Optimisation · Déclarations · Contrôles",
    lede: "Optimisez votre fiscalité et sécurisez vos déclarations avec un partenaire expert.",
    bullets: [
      "Liasses fiscales (IS, IR, BIC, BNC)",
      "Déclarations de TVA (mensuelles, trimestrielles, annuelles)",
      "Optimisation fiscale et stratégie patrimoniale",
      "Assistance en cas de contrôle fiscal",
      "Crédits d'impôt (CIR, CII) et dispositifs sectoriels",
    ],
  },
  {
    slug: "paie", icon: Users, title: "Paie & Ressources Humaines",
    tag: "Bulletins · Déclarations sociales · Droit du travail",
    lede: "Externalisez votre gestion sociale en toute confiance, sans erreur ni retard.",
    bullets: [
      "Bulletins de paie et déclarations sociales (DSN)",
      "Contrats de travail et suivi conventionnel",
      "Conseil en droit du travail au quotidien",
      "Ruptures conventionnelles, licenciements",
      "Portail RH sécurisé pour vos salariés",
    ],
  },
  {
    slug: "creation", icon: Sparkles, title: "Conseil & Création d'entreprise",
    tag: "Business plan · Statut juridique · Démarrage",
    lede: "De l'idée à l'immatriculation, nous vous accompagnons dans le lancement de votre projet.",
    bullets: [
      "Étude de faisabilité et business plan",
      "Choix du statut juridique (SASU, SARL, SAS, EURL, EI)",
      "Formalités de création et immatriculation",
      "Recherche de financement",
      "Accompagnement des 12 premiers mois",
    ],
  },
  {
    slug: "transmission", icon: HandCoins, title: "Transmission & Apport-Cession",
    tag: "Notre spécialité",
    lede: "Un accompagnement pluridisciplinaire pour sécuriser la cession de votre entreprise.",
    bullets: [
      "Diagnostic patrimonial et valorisation",
      "Structuration juridique et fiscale (art. 150-0 B ter)",
      "Coordination avec notaire, avocat, gestion de patrimoine",
      "Négociation et pilotage de la cession",
      "Stratégie de remploi des fonds",
    ],
  },
];

const ServicesPage = () => {
  const ref = useReveal();
  const loc = useLocation();
  useEffect(() => {
    if (loc.hash) {
      const el = document.getElementById(loc.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [loc]);

  return (
    <div ref={ref} data-testid="services-page">
      <section className="bg-teal-dark text-white pt-24 pb-20 md:pt-32 md:pb-24 grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 reveal">
          <p className="eyebrow text-teal-l3">Nos services</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[1.02] font-light max-w-4xl">
            Un accompagnement <em className="text-teal-l3">complet</em>, du quotidien aux opérations complexes.
          </h1>
          <p className="mt-8 max-w-2xl text-teal-l3 text-lg leading-relaxed">
            Nos cinq domaines d'expertise couvrent l'intégralité des besoins des entrepreneurs,
            TPE et PME — de la tenue comptable à la transmission d'entreprise.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-px">
          {items.map(({ slug, icon: Icon, title, tag, lede, bullets }, idx) => (
            <div
              key={slug}
              id={slug}
              data-testid={`service-detail-${slug}`}
              className="reveal grid lg:grid-cols-12 gap-10 py-14 md:py-20 border-t border-ink-200"
            >
              <div className="lg:col-span-4">
                <div className="flex items-center gap-3">
                  <span className="eyebrow text-teal-accent">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="h-px flex-1 bg-teal-dark/20" />
                </div>
                <Icon size={40} className="text-teal-accent mt-6" />
                <h2 className="mt-6 font-serif text-4xl md:text-5xl text-teal-dark leading-[1.02]">{title}</h2>
                <p className="mt-3 eyebrow text-ink-500">{tag}</p>
              </div>
              <div className="lg:col-span-8">
                <p className="text-xl md:text-2xl font-serif text-teal-dark leading-snug max-w-2xl">{lede}</p>
                <ul className="mt-8 space-y-3">
                  {bullets.map((b) => (
                    <li key={b} className="flex gap-3 items-start text-ink-800">
                      <span className="mt-2 w-1.5 h-1.5 bg-teal-accent shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${slug}`}
                  className="mt-10 btn-secondary inline-flex text-sm"
                >
                  Discuter de ce service <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-teal-l1 py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="eyebrow text-teal-accent">Un projet ?</p>
            <h3 className="mt-3 font-serif text-3xl md:text-4xl text-teal-dark">
              Parlons de vos besoins autour d'un premier échange gratuit.
            </h3>
          </div>
          <Link to="/contact" className="btn-primary" data-testid="services-bottom-cta">
            Prendre rendez-vous <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
